import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, cfmt, pfmt } from '../lib/api';
import { TID } from '../constants/testIds';
import NeighbourhoodCard from '../components/common/NeighbourhoodCard';

export default function Affordability() {
    const [form, setForm] = useState({
        annual_income: 125000, down_payment: 100000, monthly_debts: 400,
        mortgage_rate: 5.29, amortization_years: 25, other_monthly_costs: 800,
    });

    const calc = useMutation({
        mutationFn: async () => (await api.post('/affordability/calculate', form)).data,
    });

    const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: Number(e.target.value) }));

    return (
        <div>
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 pb-10">
                    <div className="overline">Affordability</div>
                    <h1 className="font-editorial text-[44px] lg:text-[64px] tracking-editorial mt-4 leading-[1.04]">
                        What can you actually afford?
                    </h1>
                    <p className="mt-6 text-[15px] text-neutral-600 max-w-2xl">
                        Bank-grade GDS/TDS math, transparent. We stress-test at 32% housing-to-income and 40% total debt-to-income — the same ratios your lender uses.
                    </p>
                </div>
            </section>

            <section>
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="border border-neutral-200 p-6">
                            <div className="overline">Your numbers</div>
                            <div className="mt-6 space-y-5">
                                {[
                                    { k: 'annual_income', l: 'Annual household income (CAD)', tid: TID.aff.income },
                                    { k: 'down_payment', l: 'Down payment available', tid: TID.aff.down },
                                    { k: 'monthly_debts', l: 'Other monthly debt payments', tid: TID.aff.debts },
                                    { k: 'mortgage_rate', l: 'Mortgage rate (% annual)', step: '0.01', tid: TID.aff.rate },
                                    { k: 'amortization_years', l: 'Amortization (years)', tid: TID.aff.amort },
                                    { k: 'other_monthly_costs', l: 'Property tax + fees (monthly)' },
                                ].map((f) => (
                                    <div key={f.k}>
                                        <label className="text-[12px] uppercase tracking-[0.14em] text-neutral-500">{f.l}</label>
                                        <input
                                            type="number"
                                            step={f.step || '1'}
                                            data-testid={f.tid}
                                            value={form[f.k]}
                                            onChange={setField(f.k)}
                                            className="mt-2 w-full border border-neutral-300 px-3 py-2.5 text-[15px] font-mono-data tabular"
                                           
                                        />
                                    </div>
                                ))}
                            </div>
                            <button
                                data-testid={TID.aff.calc}
                                onClick={() => calc.mutate()}
                                disabled={calc.isPending}
                                className="mt-6 w-full bg-brand-navy text-white py-3 text-[13.5px] hover-invert hover:bg-brand-gold hover:text-brand-navy border border-black"
                               
                            >
                                {calc.isPending ? 'Calculating…' : 'Calculate my range'}
                            </button>
                        </div>
                    </div>

                    <div className="lg:col-span-8" data-testid={TID.aff.result}>
                        {!calc.data ? (
                            <div className="border border-neutral-200 h-full min-h-[400px] flex items-center justify-center p-10">
                                <div className="max-w-md text-center">
                                    <div className="overline">Awaiting inputs</div>
                                    <div className="font-editorial text-[28px] mt-4 tracking-editorial leading-tight">Fill your numbers to see the neighbourhoods that fit.</div>
                                    <p className="mt-5 text-[14px] text-neutral-600">
                                        We combine your income, debt, down payment and the current rate to produce a defensible price ceiling — then match it against every neighbourhood in our database.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="grid grid-cols-2 lg:grid-cols-4 border-l border-t border-neutral-200">
                                    {[
                                        { l: 'Max home price', v: cfmt(calc.data.max_home_price), tone: 'brand' },
                                        { l: 'Max mortgage', v: cfmt(calc.data.max_mortgage) },
                                        { l: 'Housing budget / mo', v: cfmt(calc.data.monthly_housing_budget) },
                                        { l: 'Mortgage payment / mo', v: cfmt(calc.data.monthly_mortgage_budget) },
                                    ].map((s) => (
                                        <div key={s.l} className="border-r border-b border-neutral-200 p-5">
                                            <div className="overline">{s.l}</div>
                                            <div className={`font-mono-data text-[22px] tabular mt-2 ${s.tone === 'brand' ? 'text-[#2F6B47]' : ''}`}>{s.v}</div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-10">
                                    <div className="flex items-baseline justify-between">
                                        <div className="overline">Affordable neighbourhoods · {calc.data.affordable_count}</div>
                                        {calc.data.stretch?.length > 0 && (
                                            <div className="overline text-neutral-400">{calc.data.stretch.length} stretch options below</div>
                                        )}
                                    </div>
                                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {calc.data.affordable.map((n) => (
                                            <NeighbourhoodCard key={n.slug} n={n} sub={`Within budget · ${cfmt(n.median_price)}`} />
                                        ))}
                                    </div>
                                </div>

                                {calc.data.stretch?.length > 0 && (
                                    <div className="mt-10">
                                        <div className="overline">Stretch (within 15% of budget)</div>
                                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {calc.data.stretch.map((n) => (
                                                <NeighbourhoodCard key={n.slug} n={n} sub={`Stretch · ${cfmt(n.median_price)}`} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
